<!-- eslint-disable vue/valid-template-root -->
<script>
import NumInput from "./helpers/NumInput.vue";
import { mapGetters } from "vuex";
import { mapFields } from "vuex-map-fields";
import { scroller } from "vue-scrollto/src/scrollTo";
import LifetimeSliderInput from "@lifetime-gallagher/lifetime-slider-input";
import ToolTip from "./helpers/ToolTip.vue";
import clientConfig from "/src/config/foresters-financial";

import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
} from "mdb-vue-ui-kit";

export default {
  name: "InvestmentPanel",

  components: {
    MDBContainer,
    MDBRow,
    MDBCol,
    MDBBtn,

    NumInput,
    LifetimeSliderInput,
    ToolTip,
  },

  data() {
    return {
      clientConfig,
    };
  },

  computed: {
    ...mapGetters([
      "debugMode",
      "analyticsPageNames",
      "activeTab",
      "lastActiveTab",

      "data",
      "aboutFieldsValid",
      "typeOfBond",
      "startingDepositMinimum",
      "startingDepositMaximum",
      "extraContributionsMinimum",
      "extraContributionsMaximum",
      "availableInvestmentOptions",
    ]),

    ...mapFields([
      "investmentRisk",
      "startingDeposit",
      "investmentTerm",

      "makeRegularContributions",
      "extraContributions",
      "extraContributionFrequency",
    ]),
  },

  watch: {
    formFieldWatcher: {
      handler() {
        this.$refs.investmentPanel.$el.classList.remove("was-validated");
      },
      deep: true
    },
  },

  methods: {
    validateForm() {
      if (this.checkForm(this.$refs.investmentPanel.$el) && this.aboutFieldsValid) {
        this.clickToTab(4);
      } else {
        const scrollTo = scroller();
        const frm = document.getElementsByClassName("investment-page")[0].getElementsByClassName("form-control");
        for (const field of frm) { 
          if (!field.validity.valid) {
            scrollTo("#" + field.id, 300, {offset: -160});
            break;
          }
        }
      }
    },

    checkForm(form) {
      form.classList.add("was-validated");
      return form.checkValidity();
    },

    clickToTab(tabNum) {
      const scrollTo = scroller();
      this.$store.commit("setActiveTab", tabNum);
      scrollTo("#ffApp");

      this.$triggerIframeAnalytics({
        event: "calculator",
        calculator_name: "Investment Forecaster",
        calculator_section: this.analyticsPageNames[this.lastActiveTab],
        calculator_step: tabNum < this.lastActiveTab ? "Back" : "Continue",
      });

      if (this.debugMode)
        console.log(
          "GTM Event: calculator_name: Investment Forecaster, calculator_section: " + 
          this.analyticsPageNames[this.lastActiveTab] + 
          ", calculator_step: " + 
          (tabNum < this.lastActiveTab ? "Back" : "Continue")
        );

      // Pretend this is an onload of the next page
      setTimeout(() => {
        this.$triggerIframeAnalytics({
          event: "calculator",
          calculator_name: "Investment Forecaster",
          calculator_section: this.analyticsPageNames[this.activeTab],
          calculator_step: "Start",
        });

        if (this.debugMode)
          console.log(
            "GTM Event: calculator_name: Investment Forecaster, calculator_section: " + 
            this.analyticsPageNames[this.activeTab] + 
            ", calculator_step: Start"
          );
      }, 100);
    },
  },

};
</script>

<template>
  <MDBContainer fluid class="investment-page">

    <MDBRow class="p-0 needs-validation" tag="form" novalidate ref="investmentPanel" @submit.prevent>
      <MDBCol class="main-content-wrapper pb-4 mx-auto px-0" col="12" lg="7" md="8" sm="9">

        <MDBRow>
          <MDBCol>
            <div class="form-group about">
              <label for="investmentRisk" class="main-label" tabindex="0">
                Investment risk
              </label>
            </div>
          </MDBCol>
        </MDBRow>
        <MDBRow>
          <MDBCol>
            <div class="form-floating pb-3">
              <select
                v-model="investmentRisk"
                class="form-select form-control"
                name="investmentRisk"
                id="investmentRisk"
                required
              >
                <option value="">Please select</option>
                <option
                  v-for="(option, index) in availableInvestmentOptions"
                  :key="index"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>

              <!-- <label for="investmentRisk" v-if="investmentRisk != ''">
                Please select
              </label> -->

              <div 
                class="invalid-feedback" 
                aria-live="assertive" 
                aria-description="Please select one of the options."
              >
                Please select one of the options.
              </div>
            </div>
          </MDBCol>
        </MDBRow>

        <MDBRow>
          <MDBCol>
            <div class="form-group about">
              <label for="startingDeposit" class="main-label" tabindex="0">
                Starting deposit
              </label>
            </div>
          </MDBCol>
        </MDBRow>
        <MDBRow>
          <MDBCol>
            <NumInput
              v-model.number.lazy="startingDeposit"
              divClass="pb-3"
              cssClass="input"
              :options="{
                precision: { min: 0, max: 0 },
                valueRange: { min: startingDepositMinimum, max: startingDepositMaximum }
              }"
              name="startingDeposit"
              label="Enter amount"
              placeholder="Enter amount per year"
              :isRequired="true"
              requiredDescription="Please enter a value in this field."
              requiredMessage="Please enter a value in this field."
            />

            <small class="pb-2">
              <em>Between ${{ $formatCurrency(startingDepositMinimum) }} and ${{ $formatCurrency(startingDepositMaximum) }}.</em>
            </small>

            <small class="warning" v-if="typeOfBond === 2 && startingDeposit > 14000">
              <em>Your age pension may be impacted if your total funeral bond exceeds $14,000.</em>
            </small>
          </MDBCol>
        </MDBRow>

        <Transition name="fade">
          <div v-show="typeOfBond === 1">
            <MDBRow>
              <MDBCol>
                <div class="form-group about">
                  <label for="investmentTerm" class="main-label" tabindex="0">
                    Investment term

                    <ToolTip text="The term of your bond, at the end of your term we assume that you will take a lump sum drawdown of the remaining balance.">
                      <span class="icon-info"></span>
                    </ToolTip>
                  </label>
                </div>
              </MDBCol>
            </MDBRow>
            <MDBRow>
              <MDBCol class="pb-3">
                <NumInput
                  v-model.number.lazy="investmentTerm"
                  divClass="rhs"
                  cssClass="input"
                  :options="{
                    precision: { min: 0, max: 0 },
                    valueRange: { min: 11, max: 40 },
                    currencyDisplay: 'hidden',
                  }"
                  name="investmentTerm"
                  label="Enter amount"
                  placeholder="Enter amount"
                  :isRequired="true"
                  requiredDescription="Please enter a value in this field."
                  requiredMessage="Please enter a value in this field."
                  :addSuffix="true"
                  suffixText="years"
                />
              </MDBCol>
            </MDBRow>

            <MDBRow>
              <MDBCol>
                <LifetimeSliderInput
                  :min='11'
                  :max='40'
                  v-model='investmentTerm'
                />
              </MDBCol>
            </MDBRow>
            </div>
        </Transition>

        <MDBRow class="mt-4">
          <MDBCol>
            <div class="form-group radios-section">
              <label tabindex="0"><strong>
                Would you like to make regular contributions?
              </strong></label>

              <div class="radios-section options">
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="makeRegularContributions"
                    id="makeRegularContributionsYes"
                    :value="true"
                    v-model="makeRegularContributions"
                    required
                  />
                  <label class="form-check-label" for="makeRegularContributionsYes">
                    Yes
                  </label>
                </div>

                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="makeRegularContributions"
                    id="makeRegularContributionsNo"
                    :value="false"
                    v-model="makeRegularContributions"
                    required
                  />

                  <label class="form-check-label" for="makeRegularContributionsNo">
                    No
                  </label>

                  <div 
                    class="invalid-feedback radio-validation-fix" 
                    aria-live="assertive" 
                    aria-description="Please select one of these options."
                  >
                    Please select one of these options.
                  </div>
                </div>
              </div>
            </div>
          </MDBCol>
        </MDBRow>

        <Transition name="fade">
          <div v-show="makeRegularContributions === true">
            <MDBRow class="pt-2" :class="makeRegularContributions === null ? 'radio-fix ' : ''">
              <MDBCol>
                <div class="form-group about">
                  <label for="extraContributions" class="main-label" tabindex="0">
                    Extra contributions (up to year 10)
                  </label>
                </div>
              </MDBCol>
            </MDBRow>
            
            <MDBRow>
              <MDBCol lg="8">
                <NumInput
                  v-model.number.lazy="extraContributions"
                  divClass=""
                  cssClass="input"
                  :options="{
                    precision: { min: 0, max: 0 },
                    valueRange: { min: extraContributionsMinimum, max: extraContributionsMaximum }
                  }"
                  name="extraContributions"
                  label="Enter amount"
                  placeholder="Enter amount per year"
                  :isRequired="makeRegularContributions === true"
                  requiredDescription="Please enter a value in this field."
                  requiredMessage="Please enter a value in this field."
                />
              </MDBCol>

              <MDBCol lg="4" class="mt-4 mt-lg-0">
                <div class="form-floating mb-4 mb-lg-3">
                  <select
                    v-model="extraContributionFrequency"
                    class="form-select form-control"
                    name="extraContributionFrequency"
                    id="extraContributionFrequency"
                    :required="makeRegularContributions === true"
                  >
                    <option value="">Frequency</option>
                    <!-- <option value="fortnight">per fortnight</option> -->
                    <option value="month">per month</option>
                    <!-- <option value="quarter">per quarter</option> -->
                    <option value="year">per year</option>
                  </select>

                  <!-- <label for="extraContributionFrequency" v-if="extraContributionFrequency != ''">
                    Frequency
                  </label> -->

                  <div 
                    class="invalid-feedback" 
                    aria-live="assertive" 
                    aria-description="Please select one of the options."
                  >
                    Please select one of the options.
                  </div>
                </div>
              </MDBCol>
            </MDBRow>
          </div>
        </Transition>

      </MDBCol>
    </MDBRow>
  </MDBContainer>

  <MDBContainer fluid class="px-md-3 mt-4">
    <MDBRow class="px-2">
      <MDBCol md="11" class="main-nav-wrapper">
        <MDBRow>
          <MDBCol md="11" class="mt-3 mb-5 text-center text-sm-start">
            <MDBBtn
              class="btn-primary back mx-3 ms-sm-0 mb-3 mb-sm-0"
              @click="clickToTab(2)"
              aria-label="Back button"
            >
              Back
            </MDBBtn>
            
            <MDBBtn
              class="btn-primary mb-3 mx-3 mx-sm-0 mb-sm-0"
              @click="validateForm"
              aria-label="Continue button"
            >
              Continue
            </MDBBtn>
          </MDBCol>
        </MDBRow>
      </MDBCol>
    </MDBRow>
  </MDBContainer>
</template>